import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

const SIZE_FILTER_NAME = 'filter.v.m.custom.size_variable';
const ONE_SIZE_VALUE = 'gid://shopify/FilterSettingGroup/422969687';
const REGULAR_SIZE_VALUES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const PRODUCT_TYPE_FILTER_NAME = 'filter.p.m.custom.product_type';
const BIKINI_PRODUCT_TYPE_VALUE = 'Bikinis';

let autoAddOneSizeOnNextUpdate = false;
let regularSizeCountBeforeClick = 0;

function isOneSizeInput(input) {
  return (
    input instanceof HTMLInputElement &&
    input.name === SIZE_FILTER_NAME &&
    input.value === ONE_SIZE_VALUE
  );
}
const collectionVideoMap = new WeakSet();

function prepareCollectionVideo(video) {
  if (!(video instanceof HTMLVideoElement)) return;

  video.muted = true;
  video.defaultMuted = true;
  video.loop = true;
  video.playsInline = true;

  video.setAttribute('muted', '');
  video.setAttribute('loop', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.setAttribute('autoplay', '');
  video.setAttribute('preload', 'metadata');
}

function hideVideoPoster(video) {
  const deferredMedia = video.closest('deferred-media');
  if (!deferredMedia) return;

  const posterButton = deferredMedia.querySelector(
    '.deferred-media__poster-button:not(.video-interaction-hint)'
  );

  const playIcon = deferredMedia.querySelector('.icon-play');

  posterButton?.classList.add('hidden');
  playIcon?.classList.add('hidden');
}

function playCollectionVideo(video) {
  if (!(video instanceof HTMLVideoElement)) return;
  if (!video.isConnected) return;

  prepareCollectionVideo(video);

  const playPromise = video.play();

  if (playPromise !== undefined) {
    playPromise
      .then(() => {
        hideVideoPoster(video);
      })
      .catch(() => {
        // Autoplay blocked or video not ready yet.
        // Keep poster/play button as fallback.
      });
  }
}

const collectionVideoViewportObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      const video = entry.target;

      if (!(video instanceof HTMLVideoElement)) return;

      if (entry.isIntersecting) {
        playCollectionVideo(video);

        if (video.readyState < 2) {
          video.addEventListener(
            'canplay',
            () => playCollectionVideo(video),
            { once: true }
          );
        }
      } else {
        // Optional: pause videos outside viewport for performance.
        video.pause();
      }
    });
  },
  {
    root: null,
    rootMargin: '300px 0px',
    threshold: 0.1,
  }
);

function initCollectionVideoAutoplay(root = document) {
  const videos = [];

  if (root instanceof HTMLVideoElement) {
    videos.push(root);
  }

  if (root instanceof Element) {
    if (root.matches('video')) {
      videos.push(root);
    }

    videos.push(...root.querySelectorAll('deferred-media video, video'));
  }

  if (root === document) {
    videos.push(...document.querySelectorAll('deferred-media video, video'));
  }

  [...new Set(videos)].forEach((video) => {
    if (!(video instanceof HTMLVideoElement)) return;

    prepareCollectionVideo(video);

    if (!collectionVideoMap.has(video)) {
      collectionVideoMap.add(video);
      collectionVideoViewportObserver.observe(video);
    }

    const rect = video.getBoundingClientRect();
    const isVisible =
      rect.top < window.innerHeight &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.right > 0;

    if (isVisible) {
      playCollectionVideo(video);
    }

    // Try immediately also, for videos already visible on page load.
    playCollectionVideo(video);
  });
}

let collectionVideoAutoplayTimer = null;

function scheduleCollectionVideoAutoplay(root = document) {
  clearTimeout(collectionVideoAutoplayTimer);

  collectionVideoAutoplayTimer = setTimeout(() => {
    initCollectionVideoAutoplay(root);

    if (root !== document) {
      initCollectionVideoAutoplay(document);
    }
  }, 100);
}

const productGridObserver = new MutationObserver((mutations) => {
  let shouldInitVideos = false;

  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;

      const element = node;

      if (
        element.matches?.('.product-grid, .product-card, product-card, deferred-media, video') ||
        element.querySelector?.('.product-grid, .product-card, product-card, deferred-media, video')
      ) {
        shouldInitVideos = true;
      }
    });
  });

  if (shouldInitVideos) {
    scheduleCollectionVideoAutoplay(document);
  }
});

// document.addEventListener('DOMContentLoaded', () => {
//   scheduleCollectionVideoAutoplay(document);
// });

document.addEventListener('shopify:section:load', (event) => {
  scheduleCollectionVideoAutoplay(event.target);
});

const productGrid = document.querySelector('.product-grid') || document.body;

productGridObserver.observe(productGrid, {
  childList: true,
  subtree: true,
});

function removeOneSizeFromCurrentUrl() {
  const url = new URL(window.location.href);
  const sizes = url.searchParams
    .getAll(SIZE_FILTER_NAME)
    .filter((size) => size !== ONE_SIZE_VALUE);

  url.searchParams.delete(SIZE_FILTER_NAME);

  sizes.forEach((size) => {
    url.searchParams.append(SIZE_FILTER_NAME, size);
  });

  history.replaceState(history.state || {}, '', url.toString());
}

document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target.closest?.(`input[name="${SIZE_FILTER_NAME}"]`);

    if (!(target instanceof HTMLInputElement)) return;

    const form = target.form || document;

    regularSizeCountBeforeClick = [...form.querySelectorAll(`input[name="${SIZE_FILTER_NAME}"]:checked`)]
      .filter((input) => REGULAR_SIZE_VALUES.includes(input.value)).length;
  },
  true
);
function handleManualOneSizeClick(event) {
  let oneSizeInput = null;

  const clickedInput = event.target.closest?.(
    `input[name="${SIZE_FILTER_NAME}"][value="${ONE_SIZE_VALUE}"]`
  );

  if (clickedInput instanceof HTMLInputElement) {
    oneSizeInput = clickedInput;
  }

  if (!oneSizeInput) {
    const clickedLabel = event.target.closest?.('label');

    if (clickedLabel instanceof HTMLLabelElement) {
      // Case 1: input is inside label
      const inputInsideLabel = clickedLabel.querySelector(
        `input[name="${SIZE_FILTER_NAME}"][value="${ONE_SIZE_VALUE}"]`
      );

      if (inputInsideLabel instanceof HTMLInputElement) {
        oneSizeInput = inputInsideLabel;
      }

      // Case 2: label uses for="input-id"
      if (!oneSizeInput && clickedLabel.htmlFor) {
        const inputByFor = document.getElementById(clickedLabel.htmlFor);

        if (
          inputByFor instanceof HTMLInputElement &&
          inputByFor.name === SIZE_FILTER_NAME &&
          inputByFor.value === ONE_SIZE_VALUE
        ) {
          oneSizeInput = inputByFor;
        }
      }
    }
  }

  if (!(oneSizeInput instanceof HTMLInputElement)) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();

  const facetsForm = oneSizeInput.closest('facets-form-component');

  if (!(facetsForm instanceof FacetsFormComponent)) return;

  const url = new URL(window.location.href);

  const currentSizes = url.searchParams
    .getAll(SIZE_FILTER_NAME)
    .filter((size) => size !== ONE_SIZE_VALUE);

  const isCurrentlySelected =
    oneSizeInput.checked ||
    url.searchParams.getAll(SIZE_FILTER_NAME).includes(ONE_SIZE_VALUE);

  url.searchParams.delete(SIZE_FILTER_NAME);

  currentSizes.forEach((size) => {
    url.searchParams.append(SIZE_FILTER_NAME, size);
  });

  if (isCurrentlySelected) {
    // Manual uncheck One Size
    sessionStorage.setItem('oneSizeAutoDisabled', 'true');
    sessionStorage.removeItem('oneSizeManualEnabled');

    oneSizeInput.checked = false;
    oneSizeInput.removeAttribute('checked');
  } else {
    // Manual re-check One Size
    sessionStorage.removeItem('oneSizeAutoDisabled');
    sessionStorage.setItem('oneSizeManualEnabled', 'true');

    url.searchParams.append(SIZE_FILTER_NAME, ONE_SIZE_VALUE);

    oneSizeInput.checked = true;
    oneSizeInput.setAttribute('checked', 'checked');
  }

  autoAddOneSizeOnNextUpdate = false;
  url.searchParams.delete('page');

  facetsForm.updateFiltersByURL(url.toString());
}
function getRegularSizeKeyFromParams(params) {
  return [...new Set(params.getAll(SIZE_FILTER_NAME))]
    .filter((size) => REGULAR_SIZE_VALUES.includes(size))
    .sort()
    .join('|');
}

function syncProductTypeSelection(root = document) {
  const productTypeInputs = [
    ...root.querySelectorAll(`input[name="${PRODUCT_TYPE_FILTER_NAME}"]`)
  ].filter((input) => input instanceof HTMLInputElement);

  if (!productTypeInputs.length) return;

  const sizeInputs = [
    ...document.querySelectorAll(`input[name="${SIZE_FILTER_NAME}"]:checked`)
  ].filter((input) => input instanceof HTMLInputElement);

  const hasSizeSelected = sizeInputs.length > 0;

  // First remove only our previous custom disabled state
  productTypeInputs.forEach((input) => {
    const label =
      input.closest('label') ||
      document.querySelector(`label[for="${input.id}"]`);

    // Reset only our custom disabled state
    if (input.dataset.customTypeDisabled === 'true') {
      input.disabled = false;
      input.removeAttribute('aria-disabled');
      input.dataset.customTypeDisabled = '';
      label?.classList.remove('is-disabled');
    }

    // Remove old hidden state if it was added before
    if (input.dataset.customTypeHidden === 'true') {
      const item =
        input.closest('li') ||
        input.closest('.facets__inputs-list-item') ||
        label?.parentElement;

      item?.style.removeProperty('display');
      input.dataset.customTypeHidden = '';
    }
  });

  const bikiniInput = productTypeInputs.find(
    (input) => input.value === BIKINI_PRODUCT_TYPE_VALUE
  );

  const otherCheckedInputs = productTypeInputs.filter(
    (input) => input.value !== BIKINI_PRODUCT_TYPE_VALUE && input.checked
  );

  const isBikiniSelected = bikiniInput?.checked === true;
  const hasOtherTypeSelected = otherCheckedInputs.length > 0;

  productTypeInputs.forEach((input) => {
    const label =
      input.closest('label') ||
      document.querySelector(`label[for="${input.id}"]`);

    const isBikiniInput = input.value === BIKINI_PRODUCT_TYPE_VALUE;

    // Preserve Shopify/theme disabled state from unavailable filters
    const isAlreadyDisabledByShopify =
      input.disabled ||
      input.hasAttribute('disabled') ||
      label?.classList.contains('disabled') ||
      label?.getAttribute('aria-disabled') === 'true';

    if (isAlreadyDisabledByShopify) return;

    let shouldDisableByCustomRule = false;

    if (isBikiniInput) {
      // Disable Bikini if any other product type OR any size is selected
      shouldDisableByCustomRule = hasOtherTypeSelected || hasSizeSelected;
    } else {
      // Disable other product types when Bikini is selected
      shouldDisableByCustomRule = isBikiniSelected;
    }

    if (shouldDisableByCustomRule) {
      input.disabled = true;
      input.setAttribute('aria-disabled', 'true');
      input.dataset.customTypeDisabled = 'true';
      label?.classList.add('is-disabled');
    } else {
      input.removeAttribute('aria-disabled');
      input.dataset.customTypeDisabled = '';
      label?.classList.remove('is-disabled');
    }
  });
}

function removeOneSizeFromParams(params) {
  const sizesWithoutOneSize = params
    .getAll(SIZE_FILTER_NAME)
    .filter((size) => size !== ONE_SIZE_VALUE);

  params.delete(SIZE_FILTER_NAME);

  sizesWithoutOneSize.forEach((size) => {
    params.append(SIZE_FILTER_NAME, size);
  });
}

function getRegularSizeKeyFromDOM(root = document) {
  const selectedSizes = [...root.querySelectorAll(`input[name="${SIZE_FILTER_NAME}"]:checked`)]
    .map((input) => input.value)
    .filter((size) => REGULAR_SIZE_VALUES.includes(size));

  return [...new Set(selectedSizes)].sort().join('|');
}

function toggleSizeFilterForBikinis() {
  // const urlParams = new URLSearchParams(window.location.search);

  // const selectedProductTypes = urlParams.getAll('filter.p.m.custom.product_type');

  // const isBikiniSelected = selectedProductTypes.includes('Bikinis');

  // // Hide Size only when Bikinis is the only selected product type
  // const shouldHideSizeFilter =
  //   isBikiniSelected && selectedProductTypes.length === 1;

  // const sizeFilters = document.querySelectorAll(
  //   'accordion-custom[data-filter-param-name="filter-v-option-size"]'
  // );

  // if (!sizeFilters.length) return;

  // sizeFilters.forEach((sizeFilter) => {
  //   sizeFilter.style.display = shouldHideSizeFilter ? 'none' : '';
  // });
  const urlParams = new URLSearchParams(window.location.search);

  const selectedProductTypes = urlParams.getAll('filter.p.m.custom.product_type');
  const isBikiniSelected = selectedProductTypes.includes('Bikinis');

  // Hide regular Size only when Bikinis is the only selected product type
  const shouldHideSizeFilter =
    isBikiniSelected && selectedProductTypes.length === 1;

  const sizeFilters = document.querySelectorAll(
    'accordion-custom[data-filter-param-name="filter-v-m-custom-size_variable"]'
  );

  const bikiniTopSizeFilters = document.querySelectorAll(
    'accordion-custom[data-filter-param-name="filter-v-m-custom-bikini_size_top"]'
  );

  const bikiniBottomSizeFilters = document.querySelectorAll(
    'accordion-custom[data-filter-param-name="filter-v-m-custom-bikini_size_bottom"]'
  );

  sizeFilters.forEach((sizeFilter) => {
    sizeFilter.style.display = shouldHideSizeFilter ? 'none' : '';
  });

  // Bikini Top Size and Bikini Bottom Size should show only when Bikinis is selected
  bikiniTopSizeFilters.forEach((filter) => {
    filter.style.display = isBikiniSelected ? '' : 'none';
  });

  bikiniBottomSizeFilters.forEach((filter) => {
    filter.style.display = isBikiniSelected ? '' : 'none';
  });
}

function syncOneSizeCheckboxState() {
  const oneSizeAutoDisabled =
    sessionStorage.getItem('oneSizeAutoDisabled') === 'true';

  const oneSizeManualEnabled =
    sessionStorage.getItem('oneSizeManualEnabled') === 'true';

  // If user manually checked One Size again, do not force uncheck it
  if (oneSizeManualEnabled) return;

  // Only force uncheck if user manually disabled One Size
  if (!oneSizeAutoDisabled) return;

  removeOneSizeFromCurrentUrl();

  document
    .querySelectorAll(`input[name="${SIZE_FILTER_NAME}"][value="${ONE_SIZE_VALUE}"]`)
    .forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.removeAttribute('checked');
      }
    });
}

function isOneSizeAvailable(root = document) {
  const oneSizeInputs = root.querySelectorAll(
    `input[name="${SIZE_FILTER_NAME}"][value="${ONE_SIZE_VALUE}"]`
  );

  if (!oneSizeInputs.length) return false;

  return Array.from(oneSizeInputs).some((input) => {
    if (!(input instanceof HTMLInputElement)) return false;

    return (
      !input.disabled &&
      input.getAttribute('aria-disabled') !== 'true' &&
      !input.closest('[disabled]') &&
      !input.closest('[aria-disabled="true"]') &&
      !input.closest('.disabled') &&
      !input.closest('.is-disabled')
    );
  });
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));
    const oneSizeAutoDisabled =
      sessionStorage.getItem('oneSizeAutoDisabled') === 'true';

    const oneSizeManualEnabled =
      sessionStorage.getItem('oneSizeManualEnabled') === 'true';

    const oneSizeAvailable = isOneSizeAvailable(this.refs.facetsForm);

    if (oneSizeAutoDisabled && !oneSizeManualEnabled) {
      removeOneSizeFromParams(newParameters);
    }

    if (!oneSizeAvailable && !oneSizeManualEnabled) {
      removeOneSizeFromParams(newParameters);
    }

    let selectedSizes = newParameters.getAll(SIZE_FILTER_NAME);
    if (
      oneSizeManualEnabled &&
      !selectedSizes.includes(ONE_SIZE_VALUE)
    ) {
      newParameters.append(SIZE_FILTER_NAME, ONE_SIZE_VALUE);
      selectedSizes = newParameters.getAll(SIZE_FILTER_NAME);
    }

    const regularSizeSelected = selectedSizes.some((size) =>
      REGULAR_SIZE_VALUES.includes(size)
    );

    // Reset only when all regular sizes are cleared
    if (!regularSizeSelected) {
      sessionStorage.removeItem('oneSizeAutoDisabled');
      autoAddOneSizeOnNextUpdate = false;
    }

    // Auto-add One Size only when a regular size was just selected from empty state
    if (
      autoAddOneSizeOnNextUpdate &&
      oneSizeAvailable &&
      regularSizeSelected &&
      !selectedSizes.includes(ONE_SIZE_VALUE) &&
      !oneSizeAutoDisabled
    ) {
      newParameters.append(SIZE_FILTER_NAME, ONE_SIZE_VALUE);
    }

    // Reset this after one use
    autoAddOneSizeOnNextUpdate = false;

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
    setTimeout(toggleSizeFilterForBikinis, 300);
    setTimeout(syncOneSizeCheckboxState, 300);
    setTimeout(syncOneSizeCheckboxState, 700);
  };

  /**
   * Updates the section
   */
    async #updateSection() {
      const grid = document.querySelector('.product-grid');

      if (grid) {
        grid.classList.add('is-filter-changing');
      }

      await sectionRenderer.renderSection(this.sectionId);
      toggleSizeFilterForBikinis();
      syncOneSizeCheckboxState();
      syncProductTypeSelection();

      scheduleCollectionVideoAutoplay(document);

      requestAnimationFrame(() => {
        const newGrid = document.querySelector('.product-grid');

        if (!newGrid) return;

        newGrid.classList.remove('is-filter-changing');

        void newGrid.offsetWidth;

        newGrid.classList.add('is-filter-loaded');

        setTimeout(() => {
          newGrid.classList.remove('is-filter-loaded');
        }, 250);
        
      });
    }
  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    const nextUrl = new URL(url, window.location.origin);

    history.pushState(
      { urlParameters: nextUrl.searchParams.toString() },
      '',
      nextUrl.toString()
    );

    this.dispatchEvent(new FilterUpdateEvent(nextUrl.searchParams));
    this.#updateSection();

    setTimeout(toggleSizeFilterForBikinis, 300);
    setTimeout(syncOneSizeCheckboxState, 300);
    setTimeout(syncOneSizeCheckboxState, 700);
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}
document.addEventListener('click', handleManualOneSizeClick, true);

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  // updateFilters() {
  //   // New Code Start for Color Filter
  //   this.#handleSingleProductTypeFilter();
  //   // New Code End

  //   const facetsForm = this.closest('facets-form-component');

  //   if (!(facetsForm instanceof FacetsFormComponent)) return;

  //   facetsForm.updateFilters();
  //   this.#updateSelectedFacetSummary();
  // }
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
    setTimeout(syncProductTypeSelection, 300);
    setTimeout(syncProductTypeSelection, 700);
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters(event);
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');
    
    // New Code Start for Color Filter
    // const productTypeFilterName = 'filter.v.t.custom.color';

    // if (inputElement instanceof HTMLInputElement && inputElement.name === productTypeFilterName) {
    //   formData.delete(productTypeFilterName);
    // }
    // New Code End
  
    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }

  /**
   * Keeps Product Type metafield filter single-select only
   */
  // #handleSingleProductTypeFilter() {
  //   const productTypeFilterName = 'filter.v.t.custom.color';

  //   if (!this.refs.facetInputs) return;

  //   const productTypeInputs = this.refs.facetInputs.filter(
  //     (input) => input.name === productTypeFilterName
  //   );

  //   if (!productTypeInputs.length) return;

  //   const checkedInputs = productTypeInputs.filter((input) => input.checked);

  //   // If more than one Product Type is selected, keep only the last selected one
  //   if (checkedInputs.length > 1) {
  //     const lastCheckedInput = checkedInputs[checkedInputs.length - 1];

  //     productTypeInputs.forEach((input) => {
  //       input.checked = input === lastCheckedInput;
  //     });
  //   }
  // }
  /**
   * Keeps only Color swatch filter single-select
   * Other filters will remain unchanged
   * @param {Event} event
   */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleFacetChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleFacetChange);
  }

  /**
   * Handles facet checkbox change for desktop/mobile
   * @param {Event} event
   */
  #handleFacetChange = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;

    const isSizeFilter = target.name === SIZE_FILTER_NAME;
    const isOneSize = isSizeFilter && target.value === ONE_SIZE_VALUE;
    const isRegularSize =
      isSizeFilter && REGULAR_SIZE_VALUES.includes(target.value);



    const isProductTypeFilter = target.name === PRODUCT_TYPE_FILTER_NAME;
    const isBikiniProductType =
      isProductTypeFilter && target.value === BIKINI_PRODUCT_TYPE_VALUE;

    if (isProductTypeFilter && target.checked === true) {
      const form = target.form || document;

      const productTypeInputs = [
        ...form.querySelectorAll(`input[name="${PRODUCT_TYPE_FILTER_NAME}"]`)
      ].filter((input) => input instanceof HTMLInputElement);

      if (isBikiniProductType) {
        // If Bikini selected, remove all other product types
        productTypeInputs.forEach((input) => {
          if (input.value !== BIKINI_PRODUCT_TYPE_VALUE) {
            input.checked = false;
            input.removeAttribute('checked');
          }
        });
      } else {
        // If any other type selected, remove Bikini
        productTypeInputs.forEach((input) => {
          if (input.value === BIKINI_PRODUCT_TYPE_VALUE) {
            input.checked = false;
            input.removeAttribute('checked');
          }
        });
      }

      syncProductTypeSelection(form);
    }  

    // User selected a regular size from empty state
    if (isRegularSize && target.checked === true && regularSizeCountBeforeClick === 0) {
      sessionStorage.removeItem('oneSizeAutoDisabled');
      autoAddOneSizeOnNextUpdate = true;
    }

    // If all regular sizes are unchecked, reset
    if (isRegularSize && target.checked === false) {
      const form = target.form || document;
      const stillHasRegularSize = [...form.querySelectorAll(`input[name="${SIZE_FILTER_NAME}"]:checked`)]
        .some((input) => REGULAR_SIZE_VALUES.includes(input.value));

      if (!stillHasRegularSize) {
        sessionStorage.removeItem('oneSizeAutoDisabled');
        autoAddOneSizeOnNextUpdate = false;
      }
    }

    this.updateFilters();
  };

  /**
   * Keeps only Color swatch filter single-select
   * Other filters will remain unchanged
   * @param {Event} event
   */
  #handleSingleSelectColorFilter(event) {
    const singleSelectFilterName = 'filter.v.t.custom.color';

    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== singleSelectFilterName) return;

    const colorInputs = this.querySelectorAll(
      `input[name="${singleSelectFilterName}"]`
    );

    colorInputs.forEach((input) => {
      if (input instanceof HTMLInputElement && input !== target) {
        input.checked = false;
      }
    });
  }
}


if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
document.addEventListener('DOMContentLoaded', () => {
  toggleSizeFilterForBikinis();
  syncProductTypeSelection();
});

function initCollectionPageScripts() {
  toggleSizeFilterForBikinis();
  syncProductTypeSelection();

  scheduleCollectionVideoAutoplay(document);

  requestAnimationFrame(() => {
    scheduleCollectionVideoAutoplay(document);
  });

  setTimeout(() => scheduleCollectionVideoAutoplay(document), 300);
  setTimeout(() => scheduleCollectionVideoAutoplay(document), 800);
  setTimeout(() => scheduleCollectionVideoAutoplay(document), 1500);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionPageScripts, { once: true });
} else {
  initCollectionPageScripts();
}

window.addEventListener('load', () => {
  scheduleCollectionVideoAutoplay(document);
});
/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
